// src/pages/AwaitingHuman.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const API_URL =
  process.env.REACT_APP_API_URL ||
  window.__API_URL__ ||
  'http://localhost:3000';

function normalizePhone(p) {
  const raw = String(p || '').replace(/^whatsapp:/i, '').trim();
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) return '';
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return raw.startsWith('+') ? raw : `+${digits}`;
}

function formatDate(date) {
  const d = date ? new Date(date) : null;
  return d && !Number.isNaN(d.valueOf()) ? d.toLocaleString('en-US') : '-';
}

function groupByPhone(data) {
  const grouped = {};
  for (const msg of data) {
    const phone = msg?.sender || msg?.from || msg?.phone || 'unknown';
    if (!grouped[phone]) grouped[phone] = [];
    grouped[phone].push(msg);
  }
  Object.keys(grouped).forEach((phone) => {
    grouped[phone].sort(
      (a, b) =>
        new Date(b.receivedAt || b.createdAt || 0) -
        new Date(a.receivedAt || a.createdAt || 0)
    );
  });
  return grouped;
}

/** ====== AWAITING / ALERTS HELPERS ====== **/

function extractAlertChips(meta) {
  if (!meta) return [];
  const chips = [];
  if (meta.urgent) {
    chips.push({
      kind: 'urgent',
      label: `🚨 URGENT • score ${meta.urgencyScore ?? 0}`,
    });
  }
  if (meta.handoff) {
    chips.push({
      kind: 'handoff',
      label: '✳️ Manual handoff (*)',
    });
  }
  if (Array.isArray(meta.alerts)) {
    for (const a of meta.alerts) {
      chips.push({
        kind: 'alert',
        label: String(a?.label || a?.type || 'alert'),
      });
    }
  }
  if (Array.isArray(meta.urgentMatches)) {
    for (const m of meta.urgentMatches.slice(0, 3)) {
      chips.push({
        kind: 'match',
        label: String(m?.phrase || 'match'),
      });
    }
  }
  return chips;
}

/**
 * Regra de “aguardando humano”:
 * - Só olha para a ÚLTIMA mensagem da conversa (latest).
 * - Entra na fila se:
 *   - latest.awaitingHuman === true, OU
 *   - latest.meta.urgent / .handoff / alerts / urgentMatches, OU
 *   - latest.message === '*'
 *
 * Ou seja: mensagens antigas com '*' não seguram mais o cliente na fila
 * depois de você marcar como atendido no backend.
 */
function isAwaiting(latest) {
  if (!latest) return false;

  const meta = latest.meta || {};

  if (latest.awaitingHuman === true) return true;

  if (meta.urgent === true) return true;
  if (meta.handoff === true) return true;
  if (Array.isArray(meta.alerts) && meta.alerts.length > 0) return true;
  if (Array.isArray(meta.urgentMatches) && meta.urgentMatches.length > 0)
    return true;

  if (typeof latest.message === 'string' && latest.message.trim() === '*')
    return true;

  return false;
}

export default function AwaitingHuman() {
  const [conversations, setConversations] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setErr('');

      const res = await axios.get(`${API_URL}/messages`);

      const payload = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.items)
        ? res.data.items
        : Array.isArray(res.data?.messages)
        ? res.data.messages
        : [];

      const grouped = groupByPhone(payload);
      setConversations(grouped);
    } catch (error) {
      const status = error?.response?.status;
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Unknown error';
      setErr(`Failed to load conversations. (${status || 'network'}) ${msg}`);
      console.error('GET /messages failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    // const id = setInterval(fetchConversations, 30000);
    // return () => clearInterval(id);
  }, [fetchConversations]);

  // list of conversations whose LATEST message indicates awaiting
  const awaitingList = useMemo(() => {
    const rows = [];
    for (const [phone, msgs] of Object.entries(conversations)) {
      const latest = msgs[0] || {};
      if (isAwaiting(latest)) {
        rows.push({
          phone,
          cleanPhone: normalizePhone(phone),
          latestAt: new Date(
            latest.receivedAt || latest.createdAt || 0
          ).valueOf(),
          msgs,
        });
      }
    }
    rows.sort((a, b) => b.latestAt - a.latestAt);
    return rows;
  }, [conversations]);

  async function handleMarkAttended(phone) {
    try {
      await axios.post(`${API_URL}/messages/mark_attended`, {
        phone: normalizePhone(phone),
      });
      await fetchConversations();
    } catch (e) {
      console.error('POST /messages/mark_attended failed:', e);
      alert('Failed to mark as attended.');
    }
  }

  function renderHeaderBadges(latest) {
    const chips = extractAlertChips(latest?.meta);
    if (chips.length === 0) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
          🚨 Awaiting Human
        </span>
      );
    }
    return (
      <div className="flex items-center gap-2">
        {chips.map((c, i) => {
          const base = 'text-[11px] px-2 py-0.5 rounded border';
          if (c.kind === 'urgent')
            return (
              <span
                key={i}
                className={`${base} bg-red-100 text-red-700 border-red-200`}
              >
                {c.label}
              </span>
            );
          if (c.kind === 'handoff')
            return (
              <span
                key={i}
                className={`${base} bg-yellow-100 text-yellow-800 border-yellow-200`}
              >
                {c.label}
              </span>
            );
          return (
            <span
              key={i}
              className={`${base} bg-red-50 text-red-700 border-red-200`}
            >
              {c.label}
            </span>
          );
        })}
      </div>
    );
  }

  // --- Informational Banner (always top, standard) ---
  const InfoBanner = () => (
    <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-5">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Awaiting Human Queue
          </h2>
          <p className="mt-1 text-gray-700 text-sm">
            This page lists clients whose latest message or metadata requires
            human attention:
            <strong> urgent detection</strong>,{' '}
            <strong>manual handoff (*)</strong>, or an explicit{' '}
            <code>awaitingHuman</code> flag. Use{' '}
            <em>Mark as attended</em> to clear the flag and remove the
            conversation from this queue. Items are sorted by most recent
            activity.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-6 py-4">
      {/* Top banner */}
      <InfoBanner />

      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-semibold text-yellow-800">
          Awaiting Human Clients
        </h1>
        <div className="flex items-center gap-2">
          <button
            className="text-sm px-3 py-2 rounded bg-white border hover:bg-gray-50"
            onClick={fetchConversations}
          >
            Refresh
          </button>
          <span className="text-xs text-gray-600">
            {awaitingList.length} awaiting
          </span>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 mb-4">{err}</div>}
      {loading && <div className="text-gray-500">Loading…</div>}

      {!loading && awaitingList.length === 0 && !err && (
        <div className="text-sm text-gray-600">
          No clients awaiting human right now.
        </div>
      )}

      {!loading &&
        awaitingList.map(({ phone, cleanPhone, latestAt, msgs }) => {
          const latest = msgs[0] || {};

          return (
            <details key={phone} className="bg-white rounded shadow mb-4">
              <summary className="cursor-pointer select-none px-4 py-3 bg-yellow-50 flex justify-between items-center text-sm font-semibold">
                <div className="flex flex-col">
                  <span className="text-gray-800">👤 {cleanPhone}</span>
                  <span className="text-xs text-gray-500">
                    {msgs.length} message(s) • last at {formatDate(latestAt)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {renderHeaderBadges(latest)}
                  <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                </div>
              </summary>

              <div className="px-6 py-4 space-y-4">
                <div className="flex justify-end mb-2">
                  <button
                    className="text-sm px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    onClick={() => handleMarkAttended(phone)}
                  >
                    ✔️ Mark as attended
                  </button>
                </div>

                {latest?.meta?.urgent &&
                  Array.isArray(latest?.meta?.urgentMatches) &&
                  latest.meta.urgentMatches.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-gray-500 mb-1">
                        Triggered by:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {latest.meta.urgentMatches.map((m, idx) => (
                          <span
                            key={idx}
                            className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
                            title={`type: ${m?.type || '-'}, lang: ${
                              m?.lang || '-'
                            }, score: ${m?.score ?? 0}`}
                          >
                            {m?.phrase}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                {msgs.map((msg, index) => {
                  const side =
                    msg.origin === 'client' ? 'items-start' : 'items-end';
                  const bubble =
                    msg.origin === 'client'
                      ? 'bg-gray-200 text-gray-900'
                      : 'bg-blue-600 text-white';
                  const when = msg.receivedAt || msg.createdAt;

                  return (
                    <div key={index} className={`flex flex-col ${side}`}>
                      <div
                        className={`rounded-lg px-4 py-2 shadow max-w-xl text-sm ${bubble}`}
                      >
                        <p>
                          <strong>Message:</strong> {msg.message}
                        </p>
                        {msg.iaReply && (
                          <p className="mt-1">
                            <strong>AI Reply:</strong> {msg.iaReply}
                          </p>
                        )}
                        <p className="text-xs opacity-70 mt-1">
                          {formatDate(when)}
                        </p>

                        {msg.awaitingHuman && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {extractAlertChips(msg.meta).map((c, i) => (
                              <span
                                key={i}
                                className={`text-[11px] px-2 py-0.5 rounded border ${
                                  c.kind === 'urgent'
                                    ? 'bg-red-100 text-red-700 border-red-200'
                                    : c.kind === 'handoff'
                                    ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                    : 'bg-red-50 text-red-700 border-red-200'
                                }`}
                              >
                                {c.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          );
        })}
    </div>
  );
}
